import React, { useEffect, useRef } from 'react';
import { useFormState } from 'react-final-form';
import Loader from '../../../../Loader';
import { Select } from '../../../../ui/Select';
import {
  SelectContentContainer,
  selectPopoverStyles,
  selectStyles
} from '../styled';
import { BackButtonIconEnum } from '../../../../ui/PageHeader/types';
import { SelectList } from '../SelectList';
import { SelectRefType } from '../../../../NewSearch/NewSearchBar/types';
import useSearchModal from '../../../../../hooks/useSearchModal';
import { Input, InputTypeMap } from '../../../../ui/Input';
import useScreenSize from '../../../../../hooks/useScreenSize';
import { FormErrorField } from '../FormErrorField';
import { City, Location } from '../../../../../graphql/types';
import { fullHeightModalStyles, SelectInputWrapper } from './styled';

interface LocationsSelectProps {
  loading: boolean;
  inputStat: string;
  inputPlaceholder: string;
  selectTitleAndPlaceholder: string;
  selectCypress: string;
  modalCypress: string;
  selectValue: string;
  fieldName: string;
  errorCypress?: string;
  contentContainerCypress: string;
  radioCypress: string;
  radioName: string;
  currentValue: string;
  data: (Location | City)[];
  onSelect: (e?: React.ChangeEvent<HTMLInputElement>) => void;
}

export const LocationsSelect: React.FC<LocationsSelectProps> = ({
  loading,
  inputStat,
  inputPlaceholder,
  selectTitleAndPlaceholder,
  selectCypress,
  modalCypress,
  selectValue,
  fieldName,
  errorCypress,
  contentContainerCypress,
  radioCypress,
  radioName,
  currentValue,
  data,
  onSelect
}) => {
  const { errors, submitFailed } = useFormState();
  const { isSmall } = useScreenSize();

  const selectRef = useRef<SelectRefType>(null);
  const inputEl = useRef<HTMLInputElement | null>(null);

  const {
    searchString,
    onBlurHandler,
    onFocusHandler,
    onResetHandler,
    onChangeHandler,
    setIsSubmittedForm,
    setSearchString,
    isInputOnFocus
  } = useSearchModal(inputEl);

  useEffect(() => {
    if (!isSmall) {
      const itemData = data.find(item => item.id === currentValue);
      setSearchString(itemData?.name || '');
    }
  }, [currentValue, data, isSmall]);

  const onChangeRadioHandler = (e?: React.ChangeEvent<HTMLInputElement>) => {
    onSelect(e);
    if (selectRef.current) {
      selectRef.current?.closePopover();
    }

    if (isSmall) {
      setSearchString('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      e.preventDefault();
      setIsSubmittedForm(true);
    }
  };

  const onInputReset = (e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e?.stopPropagation();
    onResetHandler();
  };

  const getInputValue = () => {
    if (isSmall) {
      return searchString || '';
    }

    return isInputOnFocus ? searchString : searchString || selectValue || '';
  };

  const isValuesEqual = searchString?.trim() === selectValue?.trim();

  const inputComponent = (
    <SelectInputWrapper>
      <Input
        refEl={inputEl}
        value={getInputValue()}
        onKeyDown={handleKeyDown}
        type={InputTypeMap.search}
        onResetHandler={onInputReset}
        stat={inputStat}
        onChangeHandler={onChangeHandler}
        onFocusHandler={onFocusHandler}
        placeholder={inputPlaceholder}
        isShowReset={!isValuesEqual && !!searchString}
        isInsideModalWithAnimation
        onBlurHandler={onBlurHandler}
        isShowSearch={isSmall}
      />
    </SelectInputWrapper>
  );

  return (
    <div>
      <Loader loading={loading}>
        <Select
          ref={selectRef}
          width="100%"
          value={selectValue}
          placeholder={selectTitleAndPlaceholder}
          styles={selectStyles}
          modalTitle={selectTitleAndPlaceholder}
          cypress={selectCypress}
          valueTag="body_1"
          popoverStyles={selectPopoverStyles}
          stylesModal={fullHeightModalStyles}
          closeButtonAlign="left"
          closeIcon={BackButtonIconEnum.Back}
          input={!isSmall && inputComponent}
          isPlaceholderVisible
          error={submitFailed && errors?.[fieldName]}
          modalCypress={modalCypress}
          inputRef={inputEl}
          showIcon={isValuesEqual || !searchString}
          isWithHover={false}
        >
          <SelectContentContainer>
            {isSmall && inputComponent}
            <SelectList
              searchString={searchString}
              showAllList={isValuesEqual}
              data={data}
              onChange={onChangeRadioHandler}
              containerCypress={contentContainerCypress}
              radioCypress={radioCypress}
              radioName={radioName}
              currentValue={currentValue}
            />
          </SelectContentContainer>
        </Select>
      </Loader>
      {errorCypress && (
        <FormErrorField field={fieldName} cypress={errorCypress} />
      )}
    </div>
  );
};
